'use client';

import { useCallback, type ReactNode } from 'react';
import { TutorialProvider, useTutorial } from './TutorialContext';
import { TutorialWalletProvider } from './wallet-shim';
import { TutorialTransactionQueueProvider } from './tx-queue-shim';
import {
  TUTORIAL_FLOOR_INDEX,
  TUTORIAL_VIEWER,
  TUTORIAL_VIEWER_PRICE,
  TUTORIAL_DEFEND_PRICE,
} from './tutorial-board';
import {
  BUYOUT_TOTAL_MS,
} from '@/features/lobby/board-canvas/animation-timings';
import { ensureHqAudioReady, hqPlayBuyout } from '@/features/lobby/audio/hq-sounds';

/**
 * Single wrapper for the `/tutorial` route.
 *
 * Order matters:
 *   - `TutorialProvider` owns the scripted state every other shim talks to.
 *   - `TutorialWalletProvider` overrides `useWallet()` for everything below it.
 *   - `TutorialTransactionQueueProvider` overrides the action queue context
 *     so drawers' Submit calls land in the simulated runner.
 *
 * `TutorialQueueBridge` sits inside `TutorialProvider` so the simulated
 * tx queue can tell the tutorial state machine to mark the buyout
 * complete, advance to the "you own the floor" step, and close the drawer.
 */
export function TutorialProviders({ children }: { children: ReactNode }) {
  return (
    <TutorialProvider>
      <TutorialWalletProvider>
        <TutorialQueueBridge>{children}</TutorialQueueBridge>
      </TutorialWalletProvider>
    </TutorialProvider>
  );
}

function TutorialQueueBridge({ children }: { children: ReactNode }) {
  const {
    mutateSeats,
    getSeats,
    goto,
    closeDrawer,
    state,
    triggerTileAnim,
  } = useTutorial();

  const handleQueueComplete = useCallback(
    (title: string) => {
      const lower = title.toLowerCase();
      if (lower.includes('take over')) {
        // Buyout succeeded. The user explicitly asked for the
        // animation to fire IMMEDIATELY on Take Over click — the
        // previous 900 ms gap between the click and the slab
        // crumbling read as a "did anything happen?" pause. New
        // choreography:
        //
        //   t=0     Drawer closes + step advances to `own-tile-blue`
        //           (the camera director picks the new step up and
        //           starts tweening to the close ownedSeatPose).
        //   t=0     Buyout animation fires on the same tick:
        //           `triggerTileAnim('buyout')` + `hqPlayBuyout`
        //           SFX + holder flip. Price is held so the slab
        //           pins at sorted rank 0 during the shatter, then
        //           regrows in place wearing the viewer's avatar
        //           on an obsidian plinth (the "owned tile" look —
        //           the natural blue edge ring on the slab + the
        //           obsidian player-plinth cap, no extra spotlight).
        //   t=BUYOUT_TOTAL_MS + PAUSE
        //           Once the slab + new avatar are fully on-screen
        //           we hold for `POST_BUYOUT_PAUSE_MS` so the user
        //           can read the new owner before anything moves,
        //           THEN bump the price → re-sort → placement
        //           transition slides the tile to its new sorted
        //           rank. `followViewer` on the camera keeps the
        //           tile in frame as it glides.
        const previousHolder = (() => {
          const seat = getSeats()[TUTORIAL_FLOOR_INDEX];
          if (!seat) return undefined;
          return {
            address: seat.holder,
            avatarUrl: seat.holderAvatar ?? null,
            level: seat.holderLevel ?? 0,
          };
        })();
        const newHolder = {
          address: TUTORIAL_VIEWER.address,
          avatarUrl: TUTORIAL_VIEWER.avatarUrl,
          level: 6,
        };
        // Step 1 — drawer closes, step advances, buyout animation
        // fires on the same tick. The user's click is the trigger;
        // the visual response should be on the same frame.
        closeDrawer();
        goto('own-tile-blue');
        triggerTileAnim(TUTORIAL_FLOOR_INDEX, {
          kind: 'buyout',
          startedAt: performance.now(),
          previousHolder,
          newHolder,
        });
        ensureHqAudioReady();
        hqPlayBuyout();
        mutateSeats((prev) => {
          const next = [...prev];
          const seat = next[TUTORIAL_FLOOR_INDEX];
          if (seat) {
            next[TUTORIAL_FLOOR_INDEX] = {
              ...seat,
              holder: TUTORIAL_VIEWER.address,
              holderName: TUTORIAL_VIEWER.name,
              holderAvatar: TUTORIAL_VIEWER.avatarUrl,
              holderLevel: 6,
              isOwn: true,
              lastPriceChangeTime: Math.floor(Date.now() / 1000),
              claimedAt: Math.floor(Date.now() / 1000),
            };
          }
          return next;
        });
        // Step 2 — after the buyout animation has fully played out
        // (slab regrown + avatar faded in) plus a deliberate pause
        // so the user reads the new ownership, push the price bump
        // → re-sort → slide. The pause is what the user asked for:
        // "animation should happen immediately, and then a pause,
        // and then the tile moves to its new location".
        const POST_BUYOUT_PAUSE_MS = 700;
        window.setTimeout(() => {
          mutateSeats((prev) => {
            const next = [...prev];
            const seat = next[TUTORIAL_FLOOR_INDEX];
            if (seat) {
              next[TUTORIAL_FLOOR_INDEX] = {
                ...seat,
                selfAssessedPrice: TUTORIAL_VIEWER_PRICE,
                balance: TUTORIAL_VIEWER_PRICE * 4n,
              };
            }
            return next;
          });
        }, BUYOUT_TOTAL_MS + POST_BUYOUT_PAUSE_MS);
        return;
      }
      if (lower.includes('manage') || lower.includes('update price') || lower.includes('defend')) {
        // The defend path raises the price via the manage drawer.
        if (state.branch === 'defend') {
          mutateSeats((prev) => {
            const next = [...prev];
            const seat = next[TUTORIAL_FLOOR_INDEX];
            if (seat) {
              next[TUTORIAL_FLOOR_INDEX] = {
                ...seat,
                selfAssessedPrice: TUTORIAL_DEFEND_PRICE,
                balance: TUTORIAL_DEFEND_PRICE * 4n,
                lastPriceChangeTime: Math.floor(Date.now() / 1000),
              };
            }
            return next;
          });
        }
        closeDrawer();
      }
    },
    [mutateSeats, getSeats, goto, closeDrawer, state.branch, triggerTileAnim],
  );

  return (
    <TutorialTransactionQueueProvider onQueueComplete={handleQueueComplete}>
      {children}
    </TutorialTransactionQueueProvider>
  );
}
